import { useQuery } from '@apollo/client';
import { createContext, useCallback, useContext, useMemo } from 'react';
import { useCurrentUser } from '../CurrentUserContext';
import { GET_FEATURE_FLAGS_QUERY } from './operations';

const FeatureFlagsContext = createContext();

const THIRTY_MINUTES = 1000 * 60 * 30;

const FEATURE_FLAGS = {
  IN_DEVELOPMENT: 'inDevelopment',
  SCREENSHOT_MODE: 'screenshotMode',
  DISABLE_CCF_LIMIT: 'disableCcfLimit',
  MAPBOX_POC: 'mapboxPoc',
  GEOLOGY_FILTER: 'geologyFilter',
  GHG_SCOPE_3: 'ghgScope3',
  PDF_EMBEDDING: 'pdfEmbedding',
  SMART_REPORTS: 'smartReports',
  ENABLE_ASSET_REPORT_VIEW_MODE: 'enableAssetReportViewMode',
  GLOBAL_DOWNLOADS: 'globalDownloads',
  ENABLE_NEXUS_MENU: 'enableNexusMenu',
  ENABLE_CONSOLIDATED_LIVE_MODELS: 'enableConsolidatedLiveModels',
  SEARCH_ASSISTANT: 'searchAssistant',
  CONSOLIDATED_LIVE_MODELS_ADMIN: 'consolidatedLiveModelsAdmin',
  VALUATIONS_V2: 'valuationsV2',
  PORTFOLIO_ANALYSIS_WELL_COUNTS: 'portfolioAnalysisWellCounts',
};

const FeatureFlagsProvider = ({ children }) => {
  const { isAuthenticated } = useCurrentUser();
  const { data, loading } = useQuery(GET_FEATURE_FLAGS_QUERY, {
    skip: !isAuthenticated,
    pollInterval: THIRTY_MINUTES,
  });
  const { featureFlags } = data || { featureFlags: null };
  const isFlagEnabled = useCallback(flagKey => featureFlags?.[flagKey] ?? false, [featureFlags]);

  const flagsLoaded = useMemo(() => !loading && featureFlags, [loading, featureFlags]);

  // Feature Flags
  const inDevelopment = useMemo(() => isFlagEnabled(FEATURE_FLAGS.IN_DEVELOPMENT));
  const screenshotMode = useMemo(() => isFlagEnabled(FEATURE_FLAGS.SCREENSHOT_MODE));
  const disableCcfLimit = useMemo(() => isFlagEnabled(FEATURE_FLAGS.DISABLE_CCF_LIMIT));
  const mapboxPoc = useMemo(() => isFlagEnabled(FEATURE_FLAGS.MAPBOX_POC));
  const geologyFilter = useMemo(() => isFlagEnabled(FEATURE_FLAGS.GEOLOGY_FILTER));
  const ghgScope3 = useMemo(() => isFlagEnabled(FEATURE_FLAGS.GHG_SCOPE_3));
  const pdfEmbedding = useMemo(() => isFlagEnabled(FEATURE_FLAGS.PDF_EMBEDDING));
  const enableConsolidatedLiveModels = useMemo(() =>
    isFlagEnabled(FEATURE_FLAGS.ENABLE_CONSOLIDATED_LIVE_MODELS),
  );
  const smartReports = useMemo(() => isFlagEnabled(FEATURE_FLAGS.SMART_REPORTS));
  const enableAssetReportPdfViewMode = useMemo(() =>
    isFlagEnabled(FEATURE_FLAGS.ENABLE_ASSET_REPORT_VIEW_MODE),
  );
  const globalDownloads = useMemo(() => isFlagEnabled(FEATURE_FLAGS.GLOBAL_DOWNLOADS));
  const enableNexusMenu = useMemo(() => isFlagEnabled(FEATURE_FLAGS.ENABLE_NEXUS_MENU));
  const searchAssistant = useMemo(() => isFlagEnabled(FEATURE_FLAGS.SEARCH_ASSISTANT));
  const consolidatedLiveModelsAdmin = useMemo(() =>
    isFlagEnabled(FEATURE_FLAGS.CONSOLIDATED_LIVE_MODELS_ADMIN),
  );
  const valuationsV2 = useMemo(() => isFlagEnabled(FEATURE_FLAGS.VALUATIONS_V2));
  const portfolioAnalysisWellCounts = useMemo(() =>
    isFlagEnabled(FEATURE_FLAGS.PORTFOLIO_ANALYSIS_WELL_COUNTS, []),
  );

  return (
    <FeatureFlagsContext.Provider
      value={{
        flagsLoaded,
        inDevelopment,
        screenshotMode,
        disableCcfLimit,
        mapboxPoc,
        geologyFilter,
        ghgScope3,
        pdfEmbedding,
        smartReports,
        enableAssetReportPdfViewMode,
        globalDownloads,
        enableNexusMenu,
        enableConsolidatedLiveModels,
        searchAssistant,
        consolidatedLiveModelsAdmin,
        valuationsV2,
        portfolioAnalysisWellCounts,
      }}
    >
      {children}
    </FeatureFlagsContext.Provider>
  );
};

const useFeatureFlags = () => useContext(FeatureFlagsContext);

export { FeatureFlagsProvider, useFeatureFlags };
